import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi } from '../services/api';
import {
  MagnifyingGlass, CalendarBlank, FileText, Buildings, Package,
  User, X, CircleNotch, ArrowRight
} from '@phosphor-icons/react';

const ICON_MAP = {
  calendar: CalendarBlank,
  file: FileText,
  'file-text': FileText,
  building: Buildings,
  package: Package,
  user: User,
};

const TYPE_COLORS = {
  activity: '#3B82F6',
  submittal: '#8B5CF6',
  project: '#10B981',
  material: '#F59E0B',
  document: '#06B6D4',
  employee: '#EC4899',
};

export default function UniversalSearch({ aiEnabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); setTotal(0); return; }
    setLoading(true);
    try {
      const res = await aiApi.search(q);
      setResults(res.data.results || []);
      setTotal(res.data.total || 0);
    } catch {
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleSelect = (item) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    navigate(item.link);
  };

  if (!aiEnabled) return null;

  return (
    <>
      {/* Search trigger button */}
      <button data-testid="universal-search-btn" onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--surface)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--text-muted)] transition-colors text-sm">
        <MagnifyingGlass size={16} />
        <span className="hidden sm:inline">Search...</span>
        <kbd className="hidden sm:inline text-[10px] bg-[var(--surface-hover)] px-1.5 py-0.5 rounded border border-[var(--border)]">Ctrl+K</kbd>
      </button>

      {/* Search overlay */}
      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]"
          onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg bg-[var(--background)] rounded-xl shadow-2xl border border-[var(--border)] overflow-hidden"
            data-testid="universal-search-modal">
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--border)]">
              <MagnifyingGlass size={20} className="text-[var(--text-muted)] flex-shrink-0" />
              <input ref={inputRef} data-testid="universal-search-input"
                value={query} onChange={handleChange}
                placeholder="Search activities, documents, materials, submittals..."
                className="flex-1 bg-transparent text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] outline-none" />
              {loading && <CircleNotch size={16} className="animate-spin text-blue-500" />}
              <button onClick={() => setOpen(false)} className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
                <X size={16} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto">
              {results.length === 0 && query.length >= 2 && !loading && (
                <div className="py-8 text-center text-sm text-[var(--text-muted)]">
                  No results found for "{query}"
                </div>
              )}
              {results.length === 0 && query.length < 2 && (
                <div className="py-8 text-center text-sm text-[var(--text-muted)]">
                  Type at least 2 characters to search
                </div>
              )}
              {results.map((item, i) => {
                const Icon = ICON_MAP[item.icon] || FileText;
                const color = TYPE_COLORS[item.type] || '#6B7280';
                return (
                  <button key={i} data-testid={`search-result-${i}`}
                    onClick={() => handleSelect(item)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--surface-hover)] transition-colors text-left border-b border-[var(--border)]/50">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${color}15` }}>
                      <Icon size={16} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--text-primary)] truncate">{item.title}</p>
                      <p className="text-xs text-[var(--text-muted)] truncate">{item.subtitle}</p>
                    </div>
                    <span className="text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full"
                      style={{ color, backgroundColor: `${color}15` }}>
                      {item.type}
                    </span>
                    <ArrowRight size={14} className="text-[var(--text-muted)]" />
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            {total > 0 && (
              <div className="px-4 py-2 border-t border-[var(--border)] text-xs text-[var(--text-muted)]">
                {total} result{total !== 1 ? 's' : ''} found
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
